import { AccountNameOrder, type FollowUpRemindersTypes, type User } from '@/shared/lib/types/accountProfile';
import type { RoleBasedIconType } from '@/shared/lib/types/admin';
import { MedicalServices, Role, ROLE_TYPES } from '@/shared/lib/types/admin';
import type { CurrentPatient } from '@/shared/lib/types/chat';
import type { LoggedUser } from '@/shared/lib/types/graphql';
import { FeatureFlag } from '@/shared/lib/types/relyingParty';
import type { AccountsMonitoringDeviceType } from '@/shared/lib/types/remotePatientMonitoring';
import { ContactedType, RpmStatusTypes } from '@/shared/lib/types/remotePatientMonitoring';
import { useFeatureFlagStore } from '@/shared/model/FeatureFlagStore';
import BadgeIcon from '@mui/icons-material/Badge';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EventIcon from '@mui/icons-material/Event';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import moment from 'moment';
import { patientTemporaryAccessUrlPaths } from './constants';
import { getChartsBaseUrl } from './environment';
import { getCurrentDate, isBefore, isSameOrAfter, startOfDay } from './format';
import { emptyString } from './string';

export function getAccountUserFullName(user: Partial<User> | undefined, order: AccountNameOrder = AccountNameOrder.FIRST_LAST): string {
  if (!user) {
    return emptyString();
  }

  // "Nguyen, Anh" rather than "Nguyen Anh": without the comma a two-part surname and a first
  // name are indistinguishable, which is the whole reason for showing the name this way round.
  if (order === AccountNameOrder.LAST_FIRST) {
    return [user.lastName, user.firstName].filter(Boolean).join(', ') || emptyString();
  }

  return `${user.firstName || ''} ${user.lastName || ''}`;
}

// First name plus last initial, e.g. "Cynthia R." — used where a full name is too long
// to sit alongside other content but the reader still needs to know who did the work.
// Returns an empty string (not emptyString()'s single space) so callers can hide the slot entirely.
export function getAccountUserShortName(user: Partial<User> | null | undefined): string {
  const firstName = user?.firstName?.trim() ?? '';
  const lastInitial = user?.lastName?.trim().charAt(0) ?? '';

  if (!firstName) return lastInitial ? `${lastInitial}.` : '';

  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
}

export const findClosestReminderForToday = (reminders: FollowUpRemindersTypes[]) => {
  const now = getCurrentDate();
  const today = startOfDay(now);

  let closestReminder: FollowUpRemindersTypes | null = null;

  if (!reminders || reminders?.length === 0) {
    return null;
  }

  for (const reminder of reminders) {
    const reminderTime = moment(reminder.followUpOn);
    if (isSameOrAfter(reminderTime, today)) {
      if (!closestReminder || isBefore(reminderTime, moment(closestReminder.followUpOn))) {
        closestReminder = reminder;
      }
    }
  }
  return closestReminder;
};

export const getAccountRedirectUrl = (
  truentityId: string,
  serviceName: string,
  patientRpmStatus: string | null,
  accountsMonitoringDevices?: AccountsMonitoringDeviceType[],
  currentUser?: LoggedUser | null
) => {
  const currentUserMedicalServices = currentUser?.relyingParty?.medicalServices?.map(medicalService => medicalService.shortName) || [];
  const isMTMServiceAvailable = currentUserMedicalServices.includes(MedicalServices.MTM);
  const isRPMServiceAvailable = currentUserMedicalServices.includes(MedicalServices.RPM);
  const isRPMEnrolled = patientRpmStatus === RpmStatusTypes.ENROLLED && accountsMonitoringDevices && accountsMonitoringDevices?.length > 0;

  if (
    (currentUser && isMTMServiceAvailable && serviceName === MedicalServices.MTM) ||
    (!currentUser && serviceName === MedicalServices.MTM)
  ) {
    return `../../patients/${truentityId}/details/medications/triage/list`;
  } else if (
    (currentUser && isRPMServiceAvailable && serviceName === MedicalServices.RPM) ||
    (!currentUser && serviceName === MedicalServices.RPM)
  ) {
    return isRPMEnrolled
      ? `../../patients/${truentityId}/details/rpm/readings`
      : `../../patients/${truentityId}/details/rpm/patient-setup/encounter/consent-management`;
  } else if (
    currentUser &&
    currentUserMedicalServices.includes(MedicalServices.CCM) &&
    useFeatureFlagStore.getState().isFeatureEnabled(FeatureFlag.CCM_ONLY_ENROLLMENT)
  ) {
    // A pharmacy that offers CCM without RPM (D6): open the Initial Visit, as for an RPM candidate.
    return `../../patients/${truentityId}/details/rpm/patient-setup/encounter/consent-management`;
  } else {
    return `../../patients/${truentityId}/details/medications/triage/list`;
  }
};

export const getScheduleVisitPath = (schedule: FollowUpRemindersTypes): string | null => {
  const truentityId = schedule.account?.truentityId;
  if (!truentityId) return null;

  if (schedule.clinicalCareEncounter?.id) {
    return `../../patients/${truentityId}/details/rpm/clinical-care-encounter/${schedule.clinicalCareEncounter.id}/care-plan`;
  }
  if (schedule.rpmFollowupConsult?.id) {
    return `../../patients/${truentityId}/details/rpm/rpm-followup-consult/${schedule.rpmFollowupConsult.id}/care-plan`;
  }
  return `../../patients/${truentityId}/details/rpm/patient-setup/encounter/care-plan`;
};

// Same routing as getScheduleVisitPath, but absolute - for links outside the SPA (e.g. CSV export).
export const getScheduleVisitAbsolutePath = (schedule: FollowUpRemindersTypes): string | null => {
  const truentityId = schedule.account?.truentityId;
  if (!truentityId) return null;

  if (schedule.clinicalCareEncounter?.id) {
    return `/patients/${truentityId}/details/rpm/clinical-care-encounter/${schedule.clinicalCareEncounter.id}/care-plan`;
  }
  if (schedule.rpmFollowupConsult?.id) {
    return `/patients/${truentityId}/details/rpm/rpm-followup-consult/${schedule.rpmFollowupConsult.id}/care-plan`;
  }
  return `/patients/${truentityId}/details/rpm/patient-setup/encounter/care-plan`;
};

export const getScheduleSignOffAt = (schedule: FollowUpRemindersTypes): string | null | undefined => {
  if (schedule.clinicalCareEncounter) {
    return schedule.clinicalCareEncounter.signOffAt;
  }
  if (schedule.rpmFollowupConsult) {
    return schedule.rpmFollowupConsult.signOffAt;
  }
  return schedule.account?.accountSummary?.signOffAt;
};

export const getScheduleTelehealthCheckedIn = (schedule: FollowUpRemindersTypes): boolean => {
  if (schedule.clinicalCareEncounter) {
    return schedule.clinicalCareEncounter.isTelehealthVisitLaunched || false;
  }
  if (schedule.rpmFollowupConsult) {
    return schedule.rpmFollowupConsult.isTelehealthVisitLaunched || false;
  }
  return schedule.account?.accountSummary?.isTelehealthVisitLaunched || false;
};

export const getRoleTypeForCurrentUser = (currentUser: LoggedUser): string => {
  switch (currentUser.roleType) {
    case Role.PHARMACIST:
      return currentUser.title?.length > 0 ? getTitleLabel(currentUser.title) : 'Pharmacist';
    case Role.SUPER:
      return 'Super Admin';
    case Role.PHARMACIST_ADMIN:
      return 'Supervisor';
    case Role.ADT_ADMIN:
      return 'Care Provider';
    case Role.PROVIDER:
      return 'Provider';
    case Role.ADMIN:
      return 'Admin';
  }
  return 'Unknown';
};

export const getTitleLabel = (titleValue: string | undefined) => {
  const titleOption = ROLE_TYPES.find(option => option.value === titleValue);
  return titleOption ? titleOption.label : 'Unknown Title';
};

export const getAccountPrefix = (patient: CurrentPatient | null) => {
  if (patient?.gender?.toLowerCase() === 'male') return 'Mr.';
  else if (patient?.gender?.toLowerCase() === 'female') return 'Ms.';
  else return null;
};

// Pharmacy chart-access URL (ticket 86exhd4yd). The new /charts flow is credential-only —
// the patient signs in with DOB + last name + PIN — so no patient id is embedded. Pass the
// pharmacy handle (org_handle, with the relying party uuid as a fallback); both resolve on
// the backend /charts/:handle route.
export const getAccountTempAccessUrl = (relyingPartyHandle: string | undefined) => {
  if (!relyingPartyHandle || typeof relyingPartyHandle !== 'string') return '#';

  const path = patientTemporaryAccessUrlPaths.replace('${company}', relyingPartyHandle);

  const url = new URL(getChartsBaseUrl());
  url.pathname = path;
  return url.toString();
};

const roleIconMapping: Record<Exclude<Role, Role.ADT_ADMIN>, RoleBasedIconType> = {
  [Role.SUPER]: {
    Icon: ManageAccountsIcon,
    color: 'error'
  },
  [Role.PHARMACIST]: {
    Icon: PersonIcon,
    color: 'primary'
  },
  [Role.PHARMACIST_ADMIN]: {
    Icon: SupervisorAccountIcon,
    color: 'secondary'
  },
  [Role.PROVIDER]: {
    Icon: BadgeIcon,
    color: 'info'
  },
  [Role.ADMIN]: {
    Icon: EngineeringIcon,
    color: 'error'
  }
};

export const getIconDataBasedOnRole = (roleType: Role): RoleBasedIconType => {
  return roleIconMapping[roleType as keyof typeof roleIconMapping] || { Icon: null, color: null };
};

export const getAthenaSupervisingProviderId = (settings?: string | null): string | undefined => {
  if (!settings) return undefined;
  try {
    const parsed = JSON.parse(settings);
    const id = parsed?.Athena?.supervisingProviderId;
    return id ? String(id) : undefined;
  } catch {
    return undefined;
  }
};

export const isCopilotEnabled = (currentUser: LoggedUser): boolean => {
  if (!currentUser) return false;

  try {
    const settingsString = currentUser.relyingParty?.settings;
    const settingsObject = settingsString ? JSON.parse(settingsString) : {};

    return settingsObject.AdditionalFeatures?.coPilot === 'true';
  } catch (error) {
    return false;
  }
};

export const rpmSegmentStatusColor = (status?: string): string | undefined => {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (s.includes(RpmStatusTypes.ENROLLED.toLowerCase())) return 'success.main';
  if (s.includes(RpmStatusTypes.IS_CANDIDATE.toLowerCase()) || s.includes(RpmStatusTypes.SCHEDULED_FOR_ENROLLMENT.toLowerCase()))
    return 'warning.main';
  if (s.includes(RpmStatusTypes.UNENROLLED.toLowerCase())) return 'error.main';
  return undefined;
};

export const contactTypeConfig = {
  [ContactedType.PHONE]: {
    icon: PhoneIcon,
    label: 'Phone Call'
  },
  [ContactedType.IN_PERSON_VISIT]: {
    icon: PeopleIcon,
    label: 'In-Person Visit'
  },
  [ContactedType.FOLLOW_UP]: {
    icon: EventIcon,
    label: 'Follow-up'
  },
  [ContactedType.INITIAL_VISIT]: {
    icon: StickyNote2Icon,
    label: 'New Patient Visit'
  }
};
